import { DateTime } from 'luxon';
import { roundWithSteps } from './time';

// The max number of minutes to display.
export const MAX_MINUTES_TO_DISPLAY = 120;
export const WEEK_DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export function formatDateTime(dateTimeString: string, options: Intl.DateTimeFormatOptions = {}) {
  const date = DateTime.fromISO(dateTimeString);

  return date.setLocale('de-CH').toLocaleString(options);
}

type FormatDateRelativeOptions = {
  value: string;
  format?: Intl.DateTimeFormatOptions;
  replace?: (date: string) => string;
};

const DEFAULT_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  hour: 'numeric',
  minute: 'numeric',
};

export function formatDateRelative({
  value,
  format = DEFAULT_DATE_FORMAT,
  replace = (date: string) => date,
}: FormatDateRelativeOptions) {
  const locale = 'de-CH';

  const dateTime: DateTime = DateTime.fromISO(value);
  const formattedDate: string = dateTime.toLocaleString(format, { locale });
  return replace ? replace(formattedDate) : formattedDate;
}

// Format a date relative to now, using different formats depending on how many days away it is.
// If the date is within the specified number of days, use a format with the weekday and time
// (something like "Montag, 14:30" or "Heute, 10:00").
// Otherwise, use a format with the day, month, and time.
export function formatDateRelativeInWeek({
  date,
  options,
}: {
  date: DateTime;
  options: { steps?: boolean };
}): string {
  const MAX_DAYS = 7;
  const daysToStartTime = Math.ceil(date.diffNow('days').as('days'));

  const value = options.steps
    ? roundWithSteps(date).toISO({ precision: 'minutes' }) ?? ''
    : date.toISO({ precision: 'minutes' }) ?? '';

  // If close enough show with weekday and time
  if (daysToStartTime < MAX_DAYS) {
    return formatDateRelative({
      value,
      replace: replaceWithTodayAndTomorrow,
      format: { hour: 'numeric', minute: 'numeric', weekday: 'long' },
    });
  }
  // Otherwise show with day, month, and time (e.g., "12. Mär, 14:30")
  else {
    return formatDateRelative({
      value,
      format: { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' },
    });
  }
}

// Replace the weekday with "heute" or "morgen" if it's today or tomorrow.
// NOTE: this only works correctly if the formattedDate uses full weekday names.
export function replaceWithTodayAndTomorrow(formattedDate: string): string {
  const today = formatDateTime(DateTime.now().toString(), { weekday: 'long' }); // e.g., "Montag"

  // Replace the weekday with "heute" if it's today.
  const replaced = formattedDate.replace(new RegExp(`^${today}`, 'i'), 'Heute');

  // Replace the weekday with "morgen" if it's tomorrow.
  const tomorrow = formatDateTime(DateTime.now().plus({ days: 1 }).toString(), { weekday: 'long' });
  return replaced.replace(new RegExp(`^${tomorrow}`, 'i'), 'Morgen');
}
